import React, { useEffect, useRef, useState } from "react";
import { Save, Search } from "react-feather";
import { FaPlus } from "react-icons/fa";
import {
  getChargePatterns,
  getCharges,
  searchRates,
} from "../../api/finance/rateManagerApi";
import {
  getBulkDropdownData,
  getOtherRefSearchDropdown,
} from "../../api/keyvalue";
import Grid from "../../components/Grid";
import LoadingSpinner from "../../components/LoadingSpinner";
import Tab from "../../components/Tab";
import Button from "../../components/input/Button";
import Dropdown from "../../components/input/Dropdown";
import Input from "../../components/input/Input";
import SearchDropdown from "../../components/input/SearchDropdown";
import SideDrawer from "../../components/input/SideDrawer";
import {
  ResizableScrollingTextRenderer,
  StatusCellStyle,
} from "../../components/renderer/StyleRenderer";
import { FORMS, MODULES, statusById } from "../../const";
import AddRates from "../../components/finance/RateManager/AddRates";
import ButtonRenderer from "../../components/renderer/ButtonRenderer";
import Modal from "../../components/Modal";
import RateCopyModal from "../../components/finance/RateManager/RateCopyModal";
import { useSelector } from "react-redux";

const popupTypes = {
  RATE_COPY: "rate_copy",
};

const RateManager = () => {
  const auth = useSelector(
      (state) => state.user.permissions[MODULES.Finance],
    );
  const rateMasterPermission = auth[FORMS?.RATE_MANAGER?.HEADER?.form];
  const rateCopyPermission =
    rateMasterPermission?.RATE_COPY?.CREATE;
  const [tab, setTab] = useState(1);
  const gridRef = useRef();
  const [activeAccordion, setActiveAccordion] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [rahId, setRahId] = useState(null);
  const [currentSearchData, setCurrentSearchData] = useState({});
  const [updateNeeded, setUpdateNeeded] = useState(false);

  const [rateTypeOpt, setRateTypeOpt] = useState("");
  const [vehicleTypeOpt, setVehicleTypeOpt] = useState("");
  const [searchFieldReset, setSearchFieldReset] = useState(false);
  const [chargeOpt, setChargeOpt] = useState("");
  const [patternOpt, setPatternOpt] = useState([]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchResult, setSearchResult] = useState([]);
  const [searchResLoading, setSearchResLoading] = useState(false);
  const [selectedRate, setSelectedRate] = useState({});
  const [searchClicked, setSearchClicked] = useState(false);

  const [searchRateCode, setSearchRateCode] = useState("");
  const [searchRateType, setSearchRateType] = useState(null);
  const [searchCharge, setSearchCharge] = useState(null);
  const [searchPattern, setSearchPattern] = useState(null);
  const [searchCusTrans, setSearchCusTrans] = useState(null);
  const [searchRateName, setSearchRateName] = useState("");
  const [searchVehCon, setSearchVehCon] = useState(null);
  const [searchVehConType, setSearchVehConType] = useState(null);
  const [searchFromDate, setSearchFromDate] = useState("");
  const [searchToDate, setSearchToDate] = useState("");
  const [isPatternLoading, setIsPatternLoading] = useState(false);
  const modalRef = useRef(null);
  const [popUpContent, setPopUpContent] = useState(null);
  const [customerSupplier, setCustomerSupplier] = useState("");
  const [rateType, setRateType] = useState("");
  const [rahHeaderId, setRahHeaderId] = useState(null);
  const [popUpKey, setPopUpKey] = useState(1);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const charges = await getCharges();
      setChargeOpt(charges);

      const { rate_type_for: rateTypes, veh_con: vehicleTypes } =
        await getBulkDropdownData([
          {
            key: "rate_type_for",
            authenticated: true,
            authorities: [
              "RATE_MANAGER_FORM_ADD_RATE_TRANSPORTER",
              "RATE_MANAGER_FORM_ADD_RATE_CUSTOMER",
            ],
          },
          { key: "veh_con" },
        ]);
      setRateTypeOpt(rateTypes);
      setVehicleTypeOpt(vehicleTypes);

      setIsLoading(false);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (searchCharge === null) return;
      if (searchCharge === "") {
        setSearchPattern(null);
      }
      if (searchCharge) {
        setIsPatternLoading(true);
        const patterns = await getChargePatterns(searchCharge);
        setPatternOpt(patterns);
        setIsPatternLoading(false);
      }
    })();
  }, [searchCharge]);

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
    setTab(1);
  };

  const handleDrawerClose = () => {
    setEditMode(false);
    setDrawerOpen(false);
  };

  useEffect(() => {
    (async () => {
      if (updateNeeded) {
        await refreshSearch(currentSearchData);
        setUpdateNeeded(false);
      }
    })();
  }, [updateNeeded]);

  const refreshSearch = async (searchData) => {
    setSearchResLoading(true);
    setSearchClicked(true);

    const response = await searchRates({
      rahCode: searchData.rahCode?.toString(),
      rahType: searchData.rahType?.toString(),
      rahReferance: searchData.rahReferance?.toString(),
      rahName: searchData.rahName?.toString(),
      rahVehcon: searchData.rahVehcon,
      rahVehicleType: searchData.rahVehicleType?.toString(),
      charge: searchData.charge?.toString(),
      rahFromDate: searchData.rahFromDate?.toString(),
      rahToDate: searchData.rahToDate?.toString(),
    });

    const searchList = [];
    for (let rate of response) {
      searchList.push({
        ...rate,
        rahFromDate: rate.rahFromDate ? rate.rahFromDate.split("T")[0] : "",
        rahToDate: rate.rahToDate ? rate.rahToDate.split("T")[0] : "",
        rahStatus: statusById[rate.rahStatus],
        rahVehcon: rate.rahVehcon === 1 ? "Vehicle" : "Container",
      });
    }

    setSearchResult(searchList);
    setSearchResLoading(false);
    setActiveAccordion(false);
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setSearchResLoading(true);
    setSearchClicked(true);

    setCurrentSearchData({
      rahCode: searchRateCode,
      rahType: searchRateType,
      rahReferance: searchCusTrans,
      rahName: searchRateName,
      rahVehcon: searchVehCon,
      rahVehicleType: searchVehConType,
      charge: searchCharge,
      rahFromDate: searchFromDate,
      rahToDate: searchToDate,
    });

    const response = await searchRates({
      rahCode: searchRateCode?.toString(),
      rahType: searchRateType?.toString(),
      rahReferance: searchCusTrans?.toString(),
      rahName: searchRateName?.toString(),
      rahVehcon: searchVehCon,
      rahVehicleType: searchVehConType?.toString(),
      charge: searchCharge?.toString(),
      pattern: searchPattern?.toString(),
      rahFromDate: searchFromDate?.toString(),
      rahToDate: searchToDate?.toString(),
    });

    const searchList = [];
    for (let rate of response) {
      searchList.push({
        ...rate,
        rahFromDate: rate.rahFromDate ? rate.rahFromDate.split("T")[0] : "",
        rahToDate: rate.rahToDate ? rate.rahToDate.split("T")[0] : "",
        rahStatus: statusById[rate.rahStatus],
        rahVehcon: rate.rahVehcon === 1 ? "Vehicle" : "Container",
      });
    }
    setSearchResult(searchList);
    setSearchResLoading(false);
    setActiveAccordion(false);
  };

  const handleReset = (e) => {
    e.preventDefault();
    setSearchRateCode("");
    setSearchRateType(null);
    setSearchCharge(null);
    setSearchPattern([]);
    setSearchCusTrans(null);
    setSearchRateName("");
    setSearchVehCon(null);
    setSearchVehConType(null);
    setSearchFromDate("");
    setSearchToDate("");
    setSearchFieldReset(!searchFieldReset);
    setSearchResult([]);
    setSearchClicked(false);
  };

  const closePopup = () => {
    modalRef.current.close();
    setPopUpContent(null);
  };

  let popUpComponent;

  switch (popUpContent) {
    case popupTypes.RATE_COPY: {
      popUpComponent = (
        <RateCopyModal
          key={popUpKey}
          customerSupplier={customerSupplier}
          rateType={rateType}
          searchFieldReset={searchFieldReset}
          setSearchFieldReset={setSearchFieldReset}
          editMode={editMode}
          rahHeaderId={rahHeaderId}
          closePopup={closePopup}
        />
      );
      break;
    }
    default:
      popUpComponent = <></>;
  }

  return (
    <>
      <div
        className="ui attached tabular"
        style={{
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <div className="ui tabular menu top attached">
          <Tab
            Icon={Search}
            onClick={() => {
              setTab(1);
            }}
            active={tab === 1}
            label="Search"
          />
        </div>
        <Button
          authenticated={true}
          editMode={editMode}
          module={MODULES.Finance}
          form={FORMS.RATE_MANAGER.HEADER.form}
          onClick={handleDrawerOpen}
          Icon={FaPlus}
          label={"Add"}
        />
        {/* <div
          className="ui primary button"
          style={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            height: "40px",
          }}
          onClick={handleDrawerOpen}
        >
          <i className="plus icon"></i>
          <span>Add</span>
        </div> */}
      </div>
      <div
        className="ui bottom attached active tab segment"
        style={{ display: tab === 1 ? "block" : "none" }}
      >
        <div className="ui styled accordion" style={{ width: "100%" }}>
          <div
            className={`title ${activeAccordion ? "active" : ""}`}
            onClick={() => setActiveAccordion(!activeAccordion)}
          >
            <div>
              Click here to expand search
              {/* <Search size={14} style={{ marginLeft: "10px" }} /> */}
              <i className="dropdown icon"></i>
            </div>
          </div>
          <div className={`content ${activeAccordion ? "active" : ""}`}>
            {isLoading ? (
              <LoadingSpinner />
            ) : (
              <div className="ui form">
                <div className="ui segment">
                  <div className="four fields">
                    <Input
                      label="Rate Code"
                      placeholder="Rate Code"
                      value={searchRateCode}
                      onChange={(e) => setSearchRateCode(e.target.value)}
                    />
                    <Dropdown
                      label="Rate Type"
                      clearable
                      placeholder="Select Rate Type"
                      options={rateTypeOpt}
                      value={searchRateType}
                      onChange={(e, data) => setSearchRateType(data.value)}
                    />
                    <SearchDropdown
                      key={1}
                      label={"Customer/Transporter"}
                      clearable={true}
                      onClear={() => setSearchCusTrans(null)}
                      reset={searchFieldReset}
                      setReset={setSearchFieldReset}
                      onResultSelect={(data) => setSearchCusTrans(data)}
                      api={getOtherRefSearchDropdown}
                      apiDependency={{
                        type: "stakeholder_type",
                        value:
                          searchRateType === 2 ? "Transporter" : "Billing Customer",
                      }}
                      fieldInfo={{
                        tableName: "masterdata.tms_m_stakeholder",
                        primaryKey: "stk_id",
                        fieldName: "stk_name",
                        id: searchCusTrans,
                      }}
                      disabled={!searchRateType}
                    />
                    <Dropdown
                      label="Charge"
                      placeholder="Select Charge"
                      clearable
                      options={chargeOpt}
                      value={searchCharge}
                      onChange={(e, data) => setSearchCharge(data.value)}
                    />
                    <Dropdown
                      label="Patterns"
                      clearable
                      placeholder={
                        isPatternLoading ? "Loading" : "Select pattern"
                      }
                      options={patternOpt}
                      disabled={!searchCharge || isPatternLoading}
                      value={searchPattern}
                      onChange={(e, data) => {
                        const val = data.value ? JSON.parse(data.value) : {};
                        setSearchPattern(parseInt(val?.chpId));
                      }}
                    />
                  </div>
                  <div className="five fields">
                    <Input
                      label="Rate Name"
                      placeholder="Rate Name"
                      value={searchRateName}
                      onChange={(e) => setSearchRateName(e.target.value)}
                    />

                    <Dropdown
                      label="Category"
                      placeholder="Select Category"
                      options={vehicleTypeOpt}
                      value={searchVehCon}
                      onChange={(e, data) => setSearchVehCon(data.value)}
                    />

                    <SearchDropdown
                      key={1}
                      label={"Vehicle/Container types"}
                      clearable={true}
                      onClear={() => setSearchVehConType(null)}
                      reset={searchFieldReset}
                      setReset={setSearchFieldReset}
                      onResultSelect={(data) => setSearchVehConType(data)}
                      api={getOtherRefSearchDropdown}
                      threshold={1}
                      apiDependency={
                        searchVehCon === 1
                          ? {
                              type: "vehicle_type",
                              value: "",
                            }
                          : {
                              type: "container_type",
                              value: "",
                            }
                      }
                      fieldInfo={
                        searchVehCon === 1
                          ? {
                              tableName: "masterdata.tms_m_vehicle_type",
                              primaryKey: "vht_id",
                              fieldName: "vht_type",
                              id: searchVehConType,
                            }
                          : {
                              tableName: "masterdata.tms_m_container_type",
                              primaryKey: "cot_id",
                              fieldName: "cot_discription",
                              ids: searchVehConType,
                            }
                      }
                      disabled={!searchVehCon}
                    />
                    <Input
                      type="date"
                      label="From Date"
                      placeholder="From Date"
                      // min={new Date().toISOString().split("T")[0]}
                      value={searchFromDate}
                      onChange={(e) => setSearchFromDate(e.target.value)}
                    />
                    <Input
                      type="date"
                      label="To Date"
                      placeholder="To Date"
                      min={searchFromDate}
                      value={searchToDate}
                      onChange={(e) => {
                        if (e.target.value >= searchFromDate)
                          setSearchToDate(e.target.value);
                      }}
                    />
                  </div>
                  <div
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                    }}
                  >
                    <div className="field" style={{ width: "100%" }}>
                      <Button
                        template="search"
                        onClick={(e) => {
                          handleSearch(e).then(r => console.log(r));
                        }}
                      />
                      <Button
                        template="searchReset"
                        onClick={(e) => {
                          handleReset(e);
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        {searchClicked && (
          <>
            {searchResLoading ? (
              <LoadingSpinner />
            ) : (
              <Grid
                ref={gridRef}
                showDownloadButton={true}
                downloadParam={""}
                downloadAction="Rate Excel Download"
                reportName="rate_master.csv"
                downloadOnlySelected={false}
                content={searchResult}
                config={[
                  {
                    headerName: "Rate Code",
                    field: "rahCode",
                    pinned: "left",
                  },
                  {
                    headerName: "Rate Name",
                    field: "rahName",
                    width: 150,
                    cellRenderer: ResizableScrollingTextRenderer,
                  },
                  {
                    headerName: "Rate Type",
                    field: "rateType",
                    width: 150,
                  },
                  {
                    headerName: "Charge",
                    field: "rahChargeValue",
                    width: 160,
                    cellRenderer: ResizableScrollingTextRenderer,
                  },
                  {
                    headerName: "Customer/Transporter",
                    field: "rahReferanceName",
                    width: 150,
                    cellRenderer: ResizableScrollingTextRenderer,
                  },
                  {
                    headerName: "Vehicle/Container",
                    field: "rahVehcon",
                    width: 150,
                  },
                  {
                    headerName: "From Date",
                    field: "rahFromDate",
                    width: 150,
                    minWidth: 250,
                  },
                  {
                    headerName: "To Date",
                    field: "rahToDate",
                    width: 150,
                    minWidth: 250,
                  },
                  {
                    headerName: "Status",
                    field: "rahStatus",
                    minWidth: 180,
                    width: 180,
                    cellRenderer: StatusCellStyle,
                    pinned: "right",
                  },
                  {
                    headerName: "Created By",
                    field: "createdBy",
                    width: 150,
                    cellRenderer: ResizableScrollingTextRenderer,
                  },
                  {
                    field: "Copy",
                    cellRenderer: ButtonRenderer,
                    pinned: "right",
                    width: "90",
                    minWidth: "90",
                    cellRendererParams: (params) => {
                      return {
                        clickHandler: () => {
                          setCustomerSupplier(params.data.rahReferance);
                          setRateType(params.data.rahRateTypeFor);
                          setRahHeaderId(params.data.rahId);
                          setPopUpContent(popupTypes.RATE_COPY);
                          // setEditMode(true);
                          setPopUpKey((prevState) => prevState + 1);
                          modalRef.current?.showModal();
                        },
                        // label: "Copy",
                        Icon: Save,
                        className: "ui button",
                        disabled: !(params.data.rahId || rateCopyPermission),
                      };
                    },
                  },
                ]}
                onCellDoubleClicked={(props) => {
                  setEditMode(true);
                  setRahId(props.data.rahId);
                  setSelectedRate(props.data);
                  handleDrawerOpen();
                }}
              />
            )}
          </>
        )}
      </div>

      <SideDrawer
        open={drawerOpen}
        handleDrawerClose={handleDrawerClose}
        head={
          editMode ? (
            <div className="drawer-header">
              <div
                style={{ padding: "10px 16px", borderBottom: "1px solid #ccc" }}
              >
                <h3>Edit Rate Code: {selectedRate?.rahCode}</h3>
              </div>
            </div>
          ) : (
            <div className="drawer-header">
              <div
                style={{ padding: "10px 16px", borderBottom: "1px solid #ccc" }}
              >
                <h3>Add New Rate</h3>
              </div>
            </div>
          )
        }
        body={
          <AddRates
            editMode={editMode}
            rahId={rahId}
            handleDrawerClose={handleDrawerClose}
            setUpdateNeeded={setUpdateNeeded}
          />
        }
        style={{ width: "90vw", boxShadow: "none" }}
      />

      <Modal
        ref={modalRef}
        closeButton={true}
        minWidth={"400px"}
        onCloseCleanUp={() => {}}
        clickOutSide={false}
      >
        {popUpComponent}
      </Modal>
    </>
  );
};

export default RateManager;
